// ClientPicker saf mantığı + tek kopya kaynağı (2026-10-07 — müşteri seçim yüzeyleri).
// Bileşenler (`components/common/ClientPicker*.tsx`) ince kalır; burada: son kullanılanlar
// (localStorage, kullanıcı bazında), TR katlamalı yerel filtre + eşleşme vurgusu, klavye
// gezinme ve liste satırlarının (başlık/seçenek/"N daha"/ek seçenek) düz dizisi — render ile
// ok tuşları aynı diziyi okur, böylece ikisi birbirinden sapamaz.

export interface ClientPickerItem {
  id: string;
  name: string;
  /** İkincil satır (sektör, "4 set" vb.) — çağıran doldurur. */
  secondary?: string | undefined;
  /** `clients.sector` serbest metni — rapor aktarımı paket önerisi için taşınır. */
  sector?: string | null | undefined;
  /** M17 T5 — yalnız mali yıl müşterisinde (≠1); ProjectNew adım 2 notu. */
  fiscalYearStartMonth?: number | undefined;
}

export const CLIENT_PICKER_COPY = {
  placeholder: "Müşteri ara…",
  recent: "Son kullanılanlar",
  suggestions: "Öneriler",
  results: "Müşteriler",
  more: "{n} müşteri daha — aramayı daraltın",
  allLink: "Tüm müşteriler →",
  loading: "Aranıyor…",
  error: "Liste alınamadı — tekrar dene",
  retry: "Tekrar dene",
  empty: "Eşleşen müşteri yok",
  clear: "Seçimi temizle",
  clearAll: "Tümünü temizle",
  remove: "Kaldır: {name}",
  selectedCount: "{n} seçili",
} as const;

export const RECENT_CLIENTS_MAX = 5;
export const RECENT_CLIENTS_KEY_PREFIX = "mahir.clients.recent";

export function recentClientsKey(userId: string): string {
  return `${RECENT_CLIENTS_KEY_PREFIX}.${userId}`;
}

/**
 * Son kullanılanlar öğesi: id + ad; M17 T5 — mali yıl ayı yalnız ≠1 ise taşınır (ProjectNew adım 2
 * notu). Eski localStorage kayıtlarında alan yok ⇒ takvim yılı; bozuk değer düşer.
 */
function toRecentItem(c: ClientPickerItem): ClientPickerItem {
  const m = c.fiscalYearStartMonth;
  const fiscal = typeof m === "number" && Number.isInteger(m) && m >= 2 && m <= 12;
  return fiscal ? { id: c.id, name: c.name, fiscalYearStartMonth: m } : { id: c.id, name: c.name };
}

function isItem(v: unknown): v is ClientPickerItem {
  return (
    typeof v === "object" &&
    v !== null &&
    typeof (v as { id?: unknown }).id === "string" &&
    typeof (v as { name?: unknown }).name === "string"
  );
}

/** localStorage'dan son kullanılanlar — bozuk/eksik kayıt sessizce boş döner. */
export function readRecentClients(userId: string | null | undefined): ClientPickerItem[] {
  if (!userId || typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(recentClientsKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isItem).slice(0, RECENT_CLIENTS_MAX).map(toRecentItem);
  } catch {
    return [];
  }
}

/** Saf: listeyi başa ekleyerek günceller (aynı id tekilleşir, en fazla `max`). */
export function addRecentClient(
  list: readonly ClientPickerItem[],
  item: ClientPickerItem,
  max = RECENT_CLIENTS_MAX,
): ClientPickerItem[] {
  const head = toRecentItem(item);
  return [head, ...list.filter((c) => c.id !== item.id)].slice(0, max);
}

/** Seçimde çağrılır: listeyi günceller ve yazar; yazılamazsa (quota/private) sessiz. */
export function pushRecentClient(
  userId: string | null | undefined,
  item: ClientPickerItem,
): ClientPickerItem[] {
  const next = addRecentClient(readRecentClients(userId), item);
  if (userId && typeof window !== "undefined") {
    try {
      window.localStorage.setItem(recentClientsKey(userId), JSON.stringify(next));
    } catch {
      // sessiz
    }
  }
  return next;
}

/** TR katlama: "İ" → "i", "I" → "ı" (toLowerCase bunu yanlış yapar), baş/son boşluk atılır. */
export function foldTr(s: string): string {
  return s.trim().toLocaleLowerCase("tr");
}

export interface MatchSegment {
  text: string;
  hit: boolean;
}

/**
 * Eşleşen parçayı vurgulamak için metni dilimler (TR katlamalı, ilk eşleşme). Katlama uzunluk
 * değiştirmez (İ→i, I→ı tek karakter) — indeksler ham metne birebir uygulanır.
 */
export function matchSegments(text: string, query: string): MatchSegment[] {
  const q = foldTr(query);
  if (q.length === 0 || text.length === 0) return [{ text, hit: false }];
  const folded = text.toLocaleLowerCase("tr");
  if (folded.length !== text.length) return [{ text, hit: false }];
  const at = folded.indexOf(q);
  if (at < 0) return [{ text, hit: false }];
  const out: MatchSegment[] = [];
  if (at > 0) out.push({ text: text.slice(0, at), hit: false });
  out.push({ text: text.slice(at, at + q.length), hit: true });
  if (at + q.length < text.length) out.push({ text: text.slice(at + q.length), hit: false });
  return out;
}

/**
 * Yerel filtre (mode="local" ve küçük listeler): TR katlamalı alt dize; önce başlangıçtan
 * eşleşenler, sonra adla. Boş sorgu listeyi olduğu gibi (sınırlı) döner.
 */
export function filterLocalItems<T extends ClientPickerItem>(
  items: readonly T[],
  query: string,
  limit = 30,
): { items: T[]; total: number } {
  const q = foldTr(query);
  const matched = q ? items.filter((c) => foldTr(c.name).includes(q)) : [...items];
  if (q) {
    matched.sort((a, b) => {
      const sa = foldTr(a.name).startsWith(q) ? 0 : 1;
      const sb = foldTr(b.name).startsWith(q) ? 0 : 1;
      return sa - sb || a.name.localeCompare(b.name, "tr");
    });
  }
  return { items: matched.slice(0, Math.max(0, limit)), total: matched.length };
}

export type PickerNavKey = "ArrowDown" | "ArrowUp" | "Home" | "End";

export function isPickerNavKey(key: string): key is PickerNavKey {
  return key === "ArrowDown" || key === "ArrowUp" || key === "Home" || key === "End";
}

/** Ok tuşu reducer'ı — uçlarda sarar; `active` -1 (seçili yok) iken ArrowDown ilkine gider. */
export function moveActive(active: number, key: PickerNavKey, count: number): number {
  if (count <= 0) return -1;
  switch (key) {
    case "Home":
      return 0;
    case "End":
      return count - 1;
    case "ArrowDown":
      return active < 0 || active >= count - 1 ? 0 : active + 1;
    case "ArrowUp":
      return active <= 0 ? count - 1 : active - 1;
  }
}

export type PickerSection = "recent" | "suggestion" | "result";

export type PickerRow =
  | { kind: "header"; label: string }
  | { kind: "option"; item: ClientPickerItem; section: PickerSection }
  | { kind: "more"; count: number }
  | { kind: "extra"; key: string; label: string };

export interface PickerRowsArgs {
  query: string;
  recent: readonly ClientPickerItem[];
  suggestions?: readonly ClientPickerItem[] | undefined;
  results: readonly ClientPickerItem[];
  /** Sunucu toplamı — `results.length`'ten büyükse "N daha" satırı çıkar. */
  total: number;
  extraOptions?: readonly { key: string; label: string }[] | undefined;
}

/**
 * Liste satırları. Boş sorguda: son kullanılanlar + öneriler + sonuçlar (aynı id yalnız ilk
 * bölümde); sorgu varken yalnız sonuçlar (son kullanılanlar/öneriler saklanır). Ek seçenekler
 * ("+ Yeni müşteri") her durumda en sonda. Sonuç bölümü başlığı yalnız başka bir bölüm de
 * görünüyorsa çizilir (tek bölümlü listede başlık gürültü).
 */
export function buildPickerRows(args: PickerRowsArgs): PickerRow[] {
  const q = foldTr(args.query);
  const rows: PickerRow[] = [];
  const seen = new Set<string>();
  const pushSection = (
    label: string,
    items: readonly ClientPickerItem[],
    section: PickerSection,
    withHeader: boolean,
  ): void => {
    const fresh = items.filter((c) => !seen.has(c.id));
    if (fresh.length === 0) return;
    if (withHeader) rows.push({ kind: "header", label });
    for (const item of fresh) {
      seen.add(item.id);
      rows.push({ kind: "option", item, section });
    }
  };
  if (!q) {
    pushSection(CLIENT_PICKER_COPY.recent, args.recent, "recent", true);
    pushSection(CLIENT_PICKER_COPY.suggestions, args.suggestions ?? [], "suggestion", true);
  }
  const others = rows.length > 0;
  pushSection(CLIENT_PICKER_COPY.results, args.results, "result", others);
  const shown = args.results.filter((c) => c.id).length;
  if (args.total > shown) rows.push({ kind: "more", count: args.total - shown });
  for (const e of args.extraOptions ?? []) rows.push({ kind: "extra", key: e.key, label: e.label });
  return rows;
}

/** Klavyeyle gezilebilen satırların (seçenek + ek) indeksleri — `activeIndex` bunu adresler. */
export function selectableRowIndexes(rows: readonly PickerRow[]): number[] {
  const out: number[] = [];
  rows.forEach((r, i) => {
    if (r.kind === "option" || r.kind === "extra") out.push(i);
  });
  return out;
}

export function fillCopy(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? ""));
}
